
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthModal from '../../../components/feature/AuthModal';

const HeroSectionNew = () => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);

  return (
    <>
      <section className="relative min-h-[90vh] bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-200 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-100 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[70vh]">
            {/* Left Content */}
            <div className="space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-teal-100">
                <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
                <span className="text-sm font-medium text-gray-700">Save Up To 70% on Top Products</span>
              </div>

              {/* Main Heading */}
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Compare Prices.
                </h1>
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Track Deals.
                </h1>
                <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                  <span className="bg-gradient-to-r from-teal-500 to-emerald-500 bg-clip-text text-transparent">Save Smart.</span>
                </h1>
              </div>

              {/* Description */}
              <p className="text-lg text-gray-600 max-w-lg leading-relaxed">
                Ohprice monitors millions of products across top retailers, alerting you instantly when prices drop. Never overpay again.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <button 
                  onClick={() => setShowAuth(true)}
                  className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 whitespace-nowrap cursor-pointer transform hover:scale-105 active:scale-95 hover:shadow-lg flex items-center gap-2"
                >
                  Start Saving Now
                  <i className="ri-arrow-right-line"></i>
                </button>
                <button 
                  onClick={() => navigate('/shop')}
                  className="text-gray-700 hover:text-gray-900 px-6 py-4 font-medium transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2 group"
                >
                  <span className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md group-hover:shadow-lg transition-shadow">
                    <i className="ri-play-fill text-teal-500"></i>
                  </span>
                  Browse Products
                </button>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-8 pt-8 border-t border-gray-200/50">
                <div>
                  <p className="text-3xl font-bold text-gray-900">2.5M+</p>
                  <p className="text-sm text-gray-500">Products Tracked</p>
                </div>
                <div className="w-px h-12 bg-gray-200"></div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">$180M+</p>
                  <p className="text-sm text-gray-500">User Savings</p>
                </div>
                <div className="w-px h-12 bg-gray-200"></div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">500K+</p>
                  <p className="text-sm text-gray-500">Active Users</p>
                </div>
              </div>
            </div>

            {/* Right Column - Floating Cards */}
            <div className="relative flex justify-center items-center">
              {/* Main Card */}
              <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md transform rotate-2 hover:rotate-0 transition-transform duration-500">
                {/* Product Preview */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-20 h-20 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0">
                    <img 
                      src="https://readdy.ai/api/search-image?query=premium%20wireless%20noise%20cancelling%20headphones%20in%20sleek%20black%20design%20on%20pure%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition%2C%20minimal%20style&width=200&height=200&seq=hero001&orientation=squarish"
                      alt="Product"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-teal-600 font-medium mb-1">Electronics</p>
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">Sony WH-1000XM5</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold text-gray-900">$279</span>
                      <span className="text-sm text-gray-400 line-through">$399</span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">-30%</span>
                    </div>
                  </div>
                </div>

                {/* Price Chart Preview */}
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-gray-500">Price History (6 months)</span>
                    <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                      <i className="ri-arrow-down-line"></i>
                      $120 saved
                    </span>
                  </div>
                  {/* Simple Chart Visualization */}
                  <div className="h-16 flex items-end gap-1">
                    {[65, 70, 68, 75, 72, 60, 55, 50, 48, 45, 42, 40].map((height, i) => (
                      <div 
                        key={i}
                        className={`flex-1 rounded-t ${i === 11 ? 'bg-teal-500' : 'bg-teal-200'}`}
                        style={{ height: `${height}%` }}
                      ></div>
                    ))}
                  </div>
                </div>

                {/* Retailers */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 bg-teal-50 rounded-lg border border-teal-100">
                    <span className="text-sm font-medium text-gray-700">Amazon</span>
                    <span className="text-sm font-bold text-teal-600">$279</span>
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg">
                    <span className="text-sm text-gray-500">Best Buy</span>
                    <span className="text-sm text-gray-500">$329</span>
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg">
                    <span className="text-sm text-gray-500">Walmart</span>
                    <span className="text-sm text-gray-500">$349</span>
                  </div>
                </div>
              </div>

              {/* Floating Notification */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4 animate-bounce-slow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-notification-3-fill text-green-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Price Drop Alert!</p>
                    <p className="text-sm font-semibold text-gray-900">-30% Off</p>
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl shadow-lg px-4 py-3">
                <div className="flex items-center gap-2">
                  <i className="ri-fire-fill text-yellow-300"></i>
                  <span className="text-sm font-semibold">Hot Deal!</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-gray-300 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-gray-400 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} initialView="register" />

      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default HeroSectionNew;
